import { useMemo } from 'react';
import { Role, useGetCurrentUserQuery, useGetUserCoursesQuery } from '@teachur/http-client';
import type { UserCourse } from '@teachur/http-client';

const EMPTY_ARRAY: UserCourse[] = [];

const getDateTimestamp = (date?: string | null) => {
  if (!date) return 0;

  const timestamp = new Date(date).getTime();

  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getCoursePassedTimestamp = (userCourse: UserCourse) => {
  return userCourse.passedAt ? getDateTimestamp(userCourse.passedAt) : null;
};

const sortUserCoursesByOldestPassed = (courses: UserCourse[]) => {
  return [...courses].sort((a, b) => {
    const aPassedAt = getCoursePassedTimestamp(a);
    const bPassedAt = getCoursePassedTimestamp(b);

    if (aPassedAt === null && bPassedAt === null) return 0;
    if (aPassedAt === null) return 1;
    if (bPassedAt === null) return -1;

    return aPassedAt - bPassedAt;
  });
};

export const useStudentCourses = (studentId: number) => {
  const { data: currentUser } = useGetCurrentUserQuery();

  const {
    data: rawCourses,
    isLoading,
    refetch,
  } = useGetUserCoursesQuery(studentId, {
    keepPreviousData: true,
  });

  const courses = useMemo(
    () => sortUserCoursesByOldestPassed(rawCourses ?? EMPTY_ARRAY),
    [rawCourses]
  );

  return {
    courses,
    isLoading,
    refetchCourses: refetch,
    isAdmin: currentUser?.role === Role.Admin || currentUser?.role === Role.SuperAdmin,
  };
};
